import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  Badge,
  Spinner,
} from "@/Components/ui";;
import classnames from "classnames";
import toast, { Toaster } from 'react-hot-toast';
import Select from "react-select";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useUsers } from "../../hooks/useUsers";
import { useMessageForm, useSenders, useTemplates, useSiteValidation } from "../../hooks/useMessaging";
import { useBulkMessaging } from "../../hooks/useBulkMessaging";
import { useBulkFormValidation } from "../../hooks/useBulkFormValidation";
import { useMessageLength } from "../../hooks/useMessageLength";
import { mapErrors } from "../../utils/validationErrors";
import ButtonsEditor from "../../Components/Common/ButtonsEditor";
import ImageUploader from "../../Components/Common/ImageUploader";
import PreviewModal from "../../Components/BulkMessaging/PreviewModal";
import ProgressPanel from "../../Components/BulkMessaging/ProgressPanel";
import { SITE_ID } from "../../assets/meta/meta";
import { siteService } from "../../api/services/site.service";
import MessagePreview from "../../Components/Messaging/MessagePreview";

const MessageSend = () => {
  const [activeTab, setActiveTab] = useState("friendtalk"); // 'friendtalk' | 'alimtalk' | 'sms'
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);

  const {
    messageType,
    setMessageType,
    formData,
    updateFormData,
    selectedSite,
    setSelectedSite,
    selectedTemplate,
    setSelectedTemplate,
    loading,
    error,
    clearError,
    resetForm,
    sendMessage,
  } = useMessageForm(activeTab);

  // Selected users recipients
  const [selectedUsers, setSelectedUsers] = useState([]); // {id, phone_number, username, kakao_channel_linked}
  // Progress state for "전부추가"
  const [addAllProgress, setAddAllProgress] = useState({ running: false, total: 0, processed: 0, cancelled: false });
  const addAllProgressRef = React.useRef(addAllProgress);
  useEffect(() => { addAllProgressRef.current = addAllProgress; }, [addAllProgress]);

  // User search hook
  const {
    users, loading: usersLoading, error: usersError,
    hasMore, totalCount, fetchMore, resetAndFetch, fetchAll,
    resetUsers
  } = useUsers({});
  const [userSearch, setUserSearch] = useState("");
  // Filter selection type
  const [filterType, setFilterType] = useState('all'); // 'all' | 'keyword' | 'purchase' | 'reservation'
  // User filters
  const [purchaseWithin, setPurchaseWithin] = useState(''); // none | all | 1m | 3m | 6m | 12m
  const [reservationDate, setReservationDate] = useState(''); // YYYY-MM-DD
  const [friendTalkEligibility, setFriendTalkEligibility] = useState(''); // '' | 'eligible' | 'ineligible'
  const applyUserFilters = useCallback(() => {
    if (filterType === 'all') {
      resetAndFetch({
        search: '',
        purchaseWithin: '',
        reservationDate: '',
        friendTalkEligibility: friendTalkEligibility || undefined,
      });
    } else {
      resetAndFetch({
        search: filterType === 'keyword' ? userSearch : '',
        purchaseWithin: filterType === 'purchase' ? purchaseWithin : '',
        reservationDate: filterType === 'reservation' ? (reservationDate || undefined) : undefined,
        friendTalkEligibility: friendTalkEligibility || undefined,
        // channelId: friendTalkEligibility ? (selectedSite?.id || SITE_ID) : undefined,
      });
    }
  }, [filterType, userSearch, purchaseWithin, reservationDate, friendTalkEligibility, selectedSite?.id]);

  useEffect(() => {
    if (activeTab === 'friendtalk') {
      setFriendTalkEligibility('eligible');
    } else {
      setFriendTalkEligibility('');
    }
  }, [activeTab]);

  const executeUserSearch = useCallback(() => {
    applyUserFilters();
  }, [applyUserFilters]);

  const addUserRecipient = (user) => {
    if (!user?.["전화번호"]) return;
    setSelectedUsers(prev => prev.find(p => p.id === user.id) ? prev : [
      ...prev,
      {
        id: user.id,
        username: user["이름"],
        phone_number: user["전화번호"],
        kakao_channel_linked: user.kakao_channel_linked,
      }
    ]);
  };

  const addAllUsers = async () => {
    // Fetch all users first
    setAddAllProgress({ running: true, total: 0, processed: 0, cancelled: false });

    let allUsers = [];
    try {
      allUsers = await fetchAll({
        search: filterType === 'keyword' ? userSearch : '',
        purchaseWithin: filterType === 'purchase' ? purchaseWithin : '',
        reservationDate: filterType === 'reservation' ? (reservationDate || undefined) : undefined,
        friendTalkEligibility: friendTalkEligibility || undefined,
      }, {
        onProgress: (fetchedUsers, count) => {
          setAddAllProgress(prev => ({
            ...prev,
            total: count,
            processed: fetchedUsers.length,
          }));
        }
      });
    } catch (err) {
      toast.error('사용자 목록을 불러오는데 실패했습니다.');
      return;
    }

    const validUsers = allUsers.filter(u => u && u.id != null && u["전화번호"]);
    if (!validUsers.length) {
      toast.info('추가할 수신자가 없습니다.');
      return;
    }

    // Process in chunks so the UI can update progress
    const chunkSize = 200; // adjust as needed
    const total = validUsers.length;
    setAddAllProgress(prev => ({ ...prev, running: true, processed: 0, total: total, cancelled: false }));

    const existingIds = new Set(selectedUsers.map(u => u.id));
    const toAppend = [];
    for (let i = 0; i < total; i += chunkSize) {
      if (addAllProgressRef.current?.cancelled) {
        // stop processing
        break;
      }
      const chunk = validUsers.slice(i, i + chunkSize);
      // small delay so UI shows progress (optional)
      await new Promise(r => setTimeout(r, 50));

      chunk.forEach(user => {
        if (!existingIds.has(user.id)) {
          // if (
          //   (friendTalkEligibility === 'eligible' && user.is_kakao_channel === selectedSite?.kakaoChannelsId)
          //   || friendTalkEligibility !== 'eligible'
          // ) {
          toAppend.push({
            id: user.id,
            username: user["이름"],
            phone_number: user["전화번호"],
            kakao_channel_linked: user.kakao_channel_linked,
          });
          existingIds.add(user.id);
          // }
        }
      });

      setAddAllProgress(prev => ({ ...prev, processed: Math.min(i + chunk.length, total) }));
    }

    // append all collected users at once to avoid many re-renders
    setSelectedUsers(prev => [...prev, ...toAppend]);
    setAddAllProgress(prev => ({ ...prev, running: false }));
    if (addAllProgressRef.current?.cancelled) {
      toast.info('전체 추가가 취소되었습니다.');
    } else {
      toast.success(`${toAppend.length}명의 수신자를 추가했습니다.`);
    }
  };

  const cancelAddAll = () => {
    setAddAllProgress(prev => ({ ...prev, cancelled: true }));
    addAllProgressRef.current = { ...addAllProgressRef.current, cancelled: true };
  };

  const removeUserRecipient = (id) => setSelectedUsers(prev => prev.filter(u => u.id !== id));
  const clearSelectedUsers = () => setSelectedUsers([]);

  // Buttons & image (for friendtalk/alimtalk)
  const [buttons, setButtons] = useState([]); // Kakao buttons
  const [image, setImage] = useState(null); // uploaded image object

  // Channel mapping from tabs to validation channel codes
  const channel = useMemo(() => {
    if (activeTab === 'alimtalk') return 'AT';
    if (activeTab === 'friendtalk') return 'FT';
    // sms tab: classification handled below (SMS/LMS)
    return 'SM';
  }, [activeTab]);

  // Validation & length classification
  const { bytes, classification } = useMessageLength(formData.message);
  const maxRecipients = 100000; // arbitrary UI limit, adjust as needed
  const bulkValidationState = React.useMemo(() => ({
    channel: channel === 'SM' ? (classification === 'LM' ? 'LM' : 'SM') : channel,
    site_id: selectedSite?.id || SITE_ID,
    template_id: formData.template_id ? Number(formData.template_id) : undefined,
    message: formData.message,
    buttons: buttons,
    image: image ? { url: image.url, is_wide: image.is_wide } : null,
    fall_back_message: formData.fall_back_message,
    recipientsCount: selectedUsers.length,
    maxRecipients,
  }), [channel, classification, selectedSite?.id, formData.template_id, formData.message, buttons, image, formData.fall_back_message, selectedUsers.length, maxRecipients]);
  const validation = useBulkFormValidation(bulkValidationState);
  const fieldErrors = mapErrors(validation.errors || {});

  // Bulk messaging logic (progress + retry)
  const { start, cancel, reset, running, completed, progress, currentBatch, totalBatches, summary, retryFailed } = useBulkMessaging({});
  const [showProgress, setShowProgress] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const openPreview = useCallback(() => {
    if (!validation.canSend) return;
    setShowPreview(true);
  }, [validation.canSend]);

  const closePreview = () => setShowPreview(false);

  const handleConfirmSend = async () => {
    setShowPreview(false);
    // if (selectedUsers.length <= 1) {
    //   // single send existing logic
    //   try {
    //     const response = await sendMessage();
    //     if (response && response.data.success) {
    //       toast.success("메시지가 성공적으로 발송되었습니다!");
    //       resetForm();
    //       setButtons([]);
    //       setImage(null);
    //     } else {
    //       toast.error(response?.data?.message || "메시지 발송에 실패했습니다.");
    //     }
    //   } catch (err) {
    //     toast.error(err.message || "메시지 발송 중 오류가 발생했습니다.");
    //   }
    //   return;
    // }
    console.log('Bulk send data:', {
      type: bulkValidationState.channel,
      recipients: selectedUsers,
      site_id: bulkValidationState.site_id,
      template_id: bulkValidationState.template_id,
      message: bulkValidationState.message,
      buttons: bulkValidationState.buttons,
      fall_back_yn: formData.fall_back_yn,
      fall_back_message: formData.fall_back_message,
      image_url: image?.url,
      template_params: formData.template_params,
      ad_flag: formData.ad_flag,
    });

    // bulk send
    setShowProgress(true);
    await start({
      type: bulkValidationState.channel,
      recipients: selectedUsers,
      site_id: bulkValidationState.site_id,
      template_id: bulkValidationState.template_id,
      message: bulkValidationState.message,
      buttons: bulkValidationState.buttons,
      fall_back_yn: formData.fall_back_yn,
      fall_back_message: formData.fall_back_message,
      image_url: image?.url,
      template_params: formData.template_params,
      ad_flag: formData.ad_flag,
    });
  };

  const { senders, fetchSenders } = useSenders({});
  const { templates, fetchTemplates } = useTemplates({});
  const { validateSite } = useSiteValidation();

  useEffect(() => {
    if (selectedUsers.length) {
      updateFormData({ phone_number: selectedUsers[0].phone_number });
    }
  }, [selectedUsers, updateFormData]);

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.getSitesApp();
        setSites(response.data.result.sites);
        const _site = response.data.result.sites.find(s => s.id === SITE_ID);
        if (_site) setSelectedSite(_site);
      } catch (error) {
        console.error("Failed to fetch sites:", error);
        toast.error("사이트 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoadingSites(false);
      }
    };

    fetchSites();
  }, []);

  // Update message type when tab changes
  useEffect(() => {
    setMessageType(activeTab);
  }, [activeTab, setMessageType]);

  // Fetch senders and templates when site changes
  useEffect(() => {
    if (selectedSite?.id) {
      updateFormData({ site_id: selectedSite.id });
      fetchSenders({ site_id: selectedSite.id, status: 'active' });
      fetchTemplates({ site_id: selectedSite.id, template_status: 'APR' });
    }
  }, [selectedSite?.id]); // Only depend on the site ID, not the functions

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      clearError();
    }
  };

  const handleSiteChange = (selectedOption) => {
    setSelectedSite(selectedOption ? selectedOption.site : null);
    setSelectedTemplate(null);
    updateFormData({ template_id: '', message: '' });
  };

  const handleTemplateChange = (selectedOption) => {
    const template = selectedOption ? selectedOption.template : null;
    setSelectedTemplate(template);
    if (template) {
      updateFormData({
        template_id: template.id,
        message: template.template_content,
      });
    } else {
      updateFormData({ template_id: '', message: '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validation.canSend) {
      toast.error("폼을 확인해주세요.");
      return;
    }
    openPreview();
  };

  const siteOptions = sites?.map(site => ({
    value: site.id,
    label: `${site.name} (${site.subdomain})`,
    site: site,
  })) || [];

  const templateOptions = templates?.map(template => ({
    value: template.id,
    label: `${template.template_name}`,
    template: template,
  })) || [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="메시지 보내기" pageTitle="메시징" />
          <Row>
            <Col lg={12}>
              <div className="d-flex justify-content-end mb-3">
                <Button type="submit" color="primary" style={{ width: '150px' }} disabled={!validation.canSend || loading}>
                  {(loading || running) && <Spinner size="sm" className="me-1" />}전송하기
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={7}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">메시지 발송</h5>
                </CardHeader>
                <CardBody>
                  {/* Message Type Tabs */}
                  <Nav tabs className="nav-tabs-custom nav-success mb-3">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "friendtalk" })}
                        onClick={() => toggleTab("friendtalk")}
                      >
                        친구톡
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "alimtalk" })}
                        onClick={() => toggleTab("alimtalk")}
                      >
                        알림톡
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "sms" })}
                        onClick={() => toggleTab("sms")}
                      >
                        SMS/LMS
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId={activeTab}>
                      <Form onSubmit={handleSubmit}>
                        {error && (
                          <Alert color="danger" className="mb-3">
                            <strong>오류:</strong> {error}
                          </Alert>
                        )}

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>사이트 선택 *</Label>
                              <Select
                                value={siteOptions.find(option => option.value === selectedSite?.id)}
                                onChange={handleSiteChange}
                                options={siteOptions}
                                isLoading={loadingSites}
                                placeholder="사이트를 선택하세요"
                                // isClearable
                                isDisabled
                              />
                            </FormGroup>
                          </Col>

                          {(activeTab === 'alimtalk') && (
                            <Col md={6}>
                              <FormGroup>
                                <Label>템플릿 선택 *</Label>
                                <Select
                                  value={templateOptions.find(option => option.value === selectedTemplate?.id)}
                                  onChange={handleTemplateChange}
                                  options={templateOptions}
                                  placeholder="템플릿을 선택하세요"
                                  isClearable
                                  isDisabled={!selectedSite}
                                />
                              </FormGroup>
                            </Col>
                          )}
                        </Row>

                        {(activeTab === 'friendtalk') && (
                          <div className="mb-3">
                            <Label>이미지 (친구톡)</Label>
                            <ImageUploader
                              siteId={selectedSite?.id}
                              value={image}
                              onChange={setImage}
                              externalError={fieldErrors.image}
                            />
                          </div>
                        )}

                        <FormGroup>
                          <Label>메시지 내용 *</Label>
                          <Input
                            type="textarea"
                            rows={6}
                            value={formData.message}
                            onChange={(e) => updateFormData({ message: e.target.value })}
                            placeholder="메시지 내용을 입력하세요"
                          />
                          <div className="text-muted small mt-1">
                            바이트: {bytes} ({classification})
                            {validation.changedChannel && <Badge color="info" className="ms-2">자동 {validation.effectiveChannel}</Badge>}
                            {validation.classification === 'OVER' && <Badge color="danger" className="ms-2">2000바이트 초과</Badge>}
                            {fieldErrors.message && <span className="text-danger ms-2">{fieldErrors.message}</span>}
                          </div>
                        </FormGroup>

                        {(activeTab === 'alimtalk' || activeTab === 'friendtalk') && (
                          <FormGroup>
                            <ButtonsEditor
                              channel={channel === 'AT' ? 'AT' : 'FT'}
                              value={buttons}
                              onChange={setButtons}
                              externalError={fieldErrors.buttons}
                            />
                          </FormGroup>
                        )}

                        {(activeTab === 'alimtalk' || activeTab === 'friendtalk') && (
                          <Row>
                            <Col md={6}>
                              <FormGroup check>
                                <Input
                                  type="checkbox"
                                  checked={formData.fall_back_yn === 'Y'}
                                  onChange={(e) => updateFormData({ fall_back_yn: e.target.checked ? "Y" : "N" })}
                                />
                                <Label check>SMS 대체발송 사용</Label>
                              </FormGroup>
                            </Col>
                            {activeTab === 'friendtalk' && (
                              <Col md={6}>
                                <FormGroup check>
                                  <Input
                                    type="checkbox"
                                    checked={formData.ad_flag === 'Y'}
                                    onChange={(e) => updateFormData({ ad_flag: e.target.checked ? "Y" : "N" })}
                                  />
                                  <Label check>광고성 메시지 (ad_flag)</Label>
                                </FormGroup>
                              </Col>
                            )}
                          </Row>
                        )}

                        {formData.fall_back_yn === 'Y' && (activeTab === 'alimtalk' || activeTab === 'friendtalk') && (
                          <FormGroup>
                            <Label>대체발송 메시지</Label>
                            <Input
                              type="textarea"
                              rows={3}
                              value={formData.fall_back_message}
                              onChange={(e) => updateFormData({ fall_back_message: e.target.value })}
                              placeholder="카카오톡 발송 실패시 SMS로 발송할 메시지"
                            />
                          </FormGroup>
                        )}

                        <div className="d-flex justify-content-start mt-3">
                          <Button type="button" size="sm" color="danger" onClick={() => {
                            resetForm(); setButtons([]); setImage(null);
                          }}>
                            초기화
                          </Button>
                        </div>

                        {/* canSend feedback checklist */}
                        <div className="mt-3">
                          <div className="fw-semibold small mb-1">전송 가능 조건</div>
                          {(() => {
                            const checks = [];
                            const isAlim = activeTab === 'alimtalk';
                            const isFriend = activeTab === 'friendtalk';
                            const hasSite = !!selectedSite?.id;
                            const hasTemplate = !isAlim || !!formData.template_id; // alimtalk requires template
                            const hasMessage = !!formData.message?.trim();
                            const underLimit = validation.classification !== 'OVER';
                            const hasRecipient = selectedUsers.length > 0;
                            const noFieldErrors = Object.keys(validation.errors || {}).length === 0 && validation.valid;
                            const all = { hasSite, hasTemplate, hasMessage, underLimit, hasRecipient, noFieldErrors };
                            const item = (ok, label) => (
                              <div key={label} className="d-flex align-items-center small" style={{ color: ok ? '#198754' : '#dc3545' }}>
                                <span className="me-1" style={{ fontWeight: 'bold' }}>{ok ? '✔' : '✖'}</span>{label}
                              </div>
                            );
                            checks.push(item(hasSite, '사이트 선택'));
                            if (isAlim) checks.push(item(hasTemplate, '알림톡 템플릿 선택'));
                            checks.push(item(hasMessage, '메시지 작성'));
                            checks.push(item(underLimit, `길이 제한 (${bytes}바이트 / 2000)`));
                            checks.push(item(hasRecipient, `수신자 추가 (${selectedUsers.length}명)`));
                            checks.push(item(noFieldErrors, '필드 검증 통과'));
                            const unmet = Object.entries(all).filter(([k, v]) => !v).length;
                            return (
                              <div className="border rounded p-2 bg-light">
                                {checks}
                                {!validation.canSend && (
                                  <div className="small mt-2 text-danger">
                                    전송 불가: {unmet}개 조건 미충족
                                  </div>
                                )}
                                {validation.canSend && (
                                  <div className="small mt-2 text-success">모든 조건 충족. 전송 가능합니다.</div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </Form>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>

            <Col lg={5}>
              <MessagePreview
                activeTab={activeTab}
                message={formData.message}
                buttons={buttons}
                image={image}
                siteName={selectedSite?.name || "캠핑장"}
                classification={classification}
              />
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">수신자 선택</h5>
                </CardHeader>
                <CardBody>
                  {/* Filter Type Selection */}
                  <div className="mb-3">
                    <Label className="fw-semibold mb-2">검색 조건 선택</Label>
                    <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="filterType"
                          id="filterAll"
                          checked={filterType === 'all'}
                          onChange={() => {
                            setFilterType('all');
                          }}
                        />
                        <label className="form-check-label" htmlFor="filterAll">
                          전체 고객
                        </label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="filterType"
                          id="filterKeyword"
                          checked={filterType === 'keyword'}
                          onChange={() => setFilterType('keyword')}
                        />
                        <label className="form-check-label" htmlFor="filterKeyword">
                          키워드 검색
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="filterType"
                          id="filterPurchase"
                          checked={filterType === 'purchase'}
                          onChange={() => setFilterType('purchase')}
                        />
                        <label className="form-check-label" htmlFor="filterPurchase">
                          구매 기간
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="filterType"
                          id="filterReservation"
                          checked={filterType === 'reservation'}
                          onChange={() => setFilterType('reservation')}
                        />
                        <label className="form-check-label" htmlFor="filterReservation">
                          예약일 고객
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Conditional Filter Inputs */}
                  {filterType === 'keyword' && (
                    <div className="d-flex mb-3" style={{ gap: 8 }}>
                      <Input
                        type="text"
                        value={userSearch}
                        placeholder="이름, 전화번호 검색"
                        onChange={(e) => setUserSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); executeUserSearch(); } }}
                      />
                    </div>
                  )}

                  {filterType === 'purchase' && (
                    <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
                      <Label style={{ lineHeight: '32px', marginBottom: 0, minWidth: 80 }}>구매 기간:</Label>
                      <Input
                        type="select"
                        value={purchaseWithin}
                        onChange={(e) => setPurchaseWithin(e.target.value)}
                        style={{ maxWidth: 160 }}
                      >
                        <option value="">선택하세요</option>
                        <option value="all">전체 구매고객</option>
                        <option value="1m">1개월 이내</option>
                        <option value="3m">3개월 이내</option>
                        <option value="6m">6개월 이내</option>
                        <option value="12m">12개월 이내</option>
                      </Input>
                    </div>
                  )}

                  {filterType === 'reservation' && (
                    <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
                      <Label style={{ lineHeight: '32px', marginBottom: 0, minWidth: 80 }}>예약일:</Label>
                      <CommonDatePicker
                        value={reservationDate}
                        clearable
                        onChangeValue={setReservationDate}
                        style={{ maxWidth: 180 }}
                      />
                    </div>
                  )}
                  <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
                    {/* <Button 
                      type="button" 
                      color="secondary" 
                      onClick={() => {
                        setFilterType('keyword');
                        setUserSearch('');
                        setPurchaseWithin('');
                        setReservationDate('');
                        executeUserSearch();
                      }}
                    >
                      초기화
                    </Button> */}
                    <Button
                      type="button"
                      color="primary"
                      onClick={executeUserSearch}
                      disabled={usersLoading ||
                        (filterType === 'keyword' && !userSearch.trim()) ||
                        (filterType === 'purchase' && !purchaseWithin) ||
                        (filterType === 'reservation' && !reservationDate)
                      }
                    >
                      {filterType === 'all' ? '가져오기' : '검색'}
                    </Button>
                  </div>
                  <div className="mb-2" style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #ddd', padding: 6, borderRadius: 4 }}>

                    <ul className="list-unstyled mb-2">
                      {users.map(u => (
                        <li key={u.id} className="d-flex justify-content-between align-items-center mb-1" style={{ borderBottom: '1px solid #eee', paddingBottom: 4 }}>
                          <div style={{ fontSize: 12 }}>
                            <strong>{u["이름"] || u.name || u.nickname || '—'}</strong>{' '}
                            <span className="text-muted">{u["전화번호"] || '번호없음'}</span>
                            {u.kakao_channel_linked && <span className="badge bg-success ms-1" style={{ fontSize: 10 }}>채널연동</span>}
                          </div>
                          <div>
                            <Button size="sm" color="success" disabled={!u["전화번호"]} onClick={() => addUserRecipient(u)}>추가</Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {hasMore && <Button size="sm" color="secondary" onClick={() => fetchMore({
                      search: filterType === 'keyword' ? userSearch : '',
                      purchaseWithin: filterType === 'purchase' ? purchaseWithin : '',
                      reservationDate: filterType === 'reservation' ? (reservationDate || undefined) : undefined,
                      friendTalkEligibility: friendTalkEligibility || undefined,
                      // channelId: friendTalkEligibility ? (selectedSite?.id || SITE_ID) : undefined,
                    })} disabled={usersLoading}>더 불러오기</Button>}
                  </div>

                  <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                    <div className="small text-muted">총 {totalCount}건</div>

                    {users.length > 1 && !addAllProgress.running && (
                      <Button type="button" color="success" onClick={addAllUsers} disabled={usersLoading}>전부추가</Button>
                    )}

                    {addAllProgress.running && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 12 }}>
                          추가 중: {addAllProgress.processed}/{addAllProgress.total}
                        </div>
                        <div style={{ width: 120, height: 8, background: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.round((addAllProgress.processed / Math.max(1, addAllProgress.total)) * 100)}%`, height: '100%', background: '#198754' }} />
                        </div>
                        <Button size="sm" color="danger" onClick={cancelAddAll}>취소</Button>
                      </div>
                    )}
                  </div>
                  {usersError && <div className="text-danger small mb-2">{usersError}</div>}

                  {/* <FormGroup className="mb-2">
                    <Label className="mb-1">직접 입력</Label>
                    <Input
                      type="textarea"
                      rows={3}
                      value={recipientsInput}
                      onChange={(e) => setRecipientsInput(e.target.value)}
                      placeholder="여러 번호를 줄바꿈/공백/콤마로 구분 (010...)"
                    />
                  </FormGroup> */}

                  <div className="mb-2" style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #ddd', padding: 6, borderRadius: 4 }}>
                    {selectedUsers.length === 0 && <div className="text-muted">추가된 수신자 없음</div>}
                    {selectedUsers.map(u => (
                      <span key={'sel-' + u.id} className="badge bg-light text-dark me-1 mb-1" style={{ fontSize: 11 }}>
                        {u.username} {u.phone_number} {u.kakao_channel_linked ? <span className="badge bg-success ms-1" style={{ fontSize: 10 }}>f</span> : null}
                        <button type="button" onClick={() => removeUserRecipient(u.id)} style={{ border: 'none', background: 'transparent', marginLeft: 4 }}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="text-muted small mt-1">
                    총 유효 수신자 {selectedUsers.length}개 / 최대 {maxRecipients}
                    {fieldErrors.recipients && <span className="text-danger ms-2">{fieldErrors.recipients}</span>}
                  </div>

                  <div className="d-flex justify-content-start mt-2">
                    <Button size="sm" color="danger" onClick={() => {
                      setPurchaseWithin(''); setReservationDate(''); clearSelectedUsers(); resetUsers();
                    }}>선택 초기화</Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <div className="d-flex justify-content-end mb-3">
                <Button type="submit" color="primary" style={{ width: '150px' }} disabled={!validation.canSend || loading}>
                  {(loading || running) && <Spinner size="sm" className="me-1" />}전송하기
                </Button>
              </div>
            </Col>
          </Row>

        </Container>
      </div>
      <Toaster position="top-center" />
      <PreviewModal
        open={showPreview}
        onClose={closePreview}
        onConfirm={handleConfirmSend}
        channel={bulkValidationState.channel}
        recipients={selectedUsers}
        message={formData.message}
        templateProvided={!!bulkValidationState.template_id}
        disabled={!validation.canSend}
      />
      <ProgressPanel
        open={showProgress}
        type={bulkValidationState.channel}
        items={progress.map(p => ({ userId: p.userId, phone: p.phone, status: p.status, error: p.error, message_id: p.message_id }))}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        running={running}
        summary={summary}
        onRetryFailed={() => retryFailed({
          type: bulkValidationState.channel,
          recipients: selectedUsers,
          site_id: bulkValidationState.site_id,
          template_id: bulkValidationState.template_id,
          message: bulkValidationState.message,
          buttons: bulkValidationState.buttons,
          fall_back_yn: formData.fall_back_yn,
          fall_back_message: formData.fall_back_message,
          image_url: image?.url,
          template_params: formData.template_params,
          ad_flag: formData.ad_flag,
        })}
        onCancel={() => { cancel(); }}
        onClose={() => setShowProgress(false)}
      />
    </React.Fragment>
  );
};

export default MessageSend;
